import { Dialog, Disclosure, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MinusIcon, PlusIcon, FunnelIcon } from "@heroicons/react/20/solid";
import { sendAuthenticatedRequest } from "../../common/functions";
import AsyncSelect from "react-select/async";
import { useShapes } from "../../buyerStateDefs";

/** Compliance attributes buyers filter on (functions/src/product-compliance.ts). */
export const COMPLIANCE_FILTER_OPTIONS = [
  { value: "dfarsCompliant", label: "DFARS compliant" },
  { value: "domesticMelt", label: "Domestic melt (USA)" },
  { value: "singleHeat", label: "Single heat" },
  { value: "mtrAvailable", label: "MTR / certs available" },
];

type FilterSection = {
  id: string;
  type: string;
  name: string;
  /** Shown above the options, where the choice needs explaining. */
  note?: string;
  options?: { value: string; label: string; checked: boolean }[];
};

export const FilterPanel = (props: FilterPanelProps) => {
  const shapes = useShapes((state) => state.shapes);
  const [materialSuggestions, setMaterialSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (
      props.structuredQuery?.grade &&
      !props.appliedFilters.find(
        (f) =>
          f.filter === "material" && f.value === props.structuredQuery.grade,
      )
    ) {
      setMaterialSuggestions([props.structuredQuery.grade]);
    } else {
      setMaterialSuggestions([]);
    }
  }, [props.appliedFilters, props.structuredQuery]);

  if (!shapes) {
    return null;
  }

  // Only the specs this query actually named. With none, the section would be an
  // empty box inviting a question nobody asked.
  const requestedStandards: string[] = Array.isArray(
    props.structuredQuery?.requestedStandards,
  )
    ? props.structuredQuery.requestedStandards
    : [];

  const filters: FilterSection[] = [
    {
      id: "material",
      type: "multiselect",
      name: "Material",
    },
    {
      id: "shape",
      type: "checkbox",
      name: "Shape",
      options: shapes.map((m) => {
        return {
          value: m,
          label: m,
          checked:
            props.appliedFilters.filter(
              (f) => f.filter === "shape" && f.value === m,
            ).length > 0,
        };
      }),
    },
    ...(requestedStandards.length > 0
      ? [
          {
            id: "requireStandards",
            type: "checkbox",
            name: "Standard",
            note:
              `You asked for ${requestedStandards.join(
                " and ",
              )}. We rank items ` +
              "certified to it first, but we also show close alternatives in " +
              "the same alloy — tick this to leave those out and see only " +
              "items whose paperwork names it.",
            options: [
              {
                value: "true",
                label: `Only items certified to ${requestedStandards.join(
                  " and ",
                )}`,
                checked: props.appliedFilters.some(
                  (f) => f.filter === "requireStandards" && f.value === "true",
                ),
              },
            ],
          },
        ]
      : []),
    {
      id: "compliance",
      type: "checkbox",
      name: "Compliance",
      note:
        "Only items whose vendor states the requirement. Items that say " +
        "nothing are left out while a box is ticked.",
      options: COMPLIANCE_FILTER_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        checked: props.appliedFilters.some(
          (f) => f.filter === o.value && f.value === "true",
        ),
      })),
    },
  ];

  const toggleSectionFilter = (sectionId: string, optionValue: string) => {
    // Compliance options are separate filters (one flag each, value "true").
    const [filter, value] =
      sectionId === "compliance" ? [optionValue, "true"] : [sectionId, optionValue];
    const found = props.appliedFilters.find(
      (f) => f.filter === filter && f.value === value,
    );
    if (found) {
      props.onChanged(props.appliedFilters.filter((f) => f !== found));
    } else {
      props.onChanged([...props.appliedFilters, { filter, value }]);
    }
  };

  const updateAllSectionFilters = (filter: string, values: string[]) => {
    const newFilters = props.appliedFilters.filter((f) => f.filter !== filter);
    values.forEach((value) => {
      newFilters.push({ filter, value });
    });
    props.onChanged(newFilters);
  };

  return (
    <div>
      {/* Mobile filter dialog */}
      <Transition.Root show={props.mobileFiltersOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden"
          onClose={props.setMobileFiltersOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                    onClick={() => props.setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Filters */}
                <form className="mt-4 border-t border-gray-200">
                  {filters.map((section) => (
                    <Disclosure
                      as="div"
                      key={section.id}
                      // The standards choice only appears when the query named a
                      // spec, and it is the one a buyer needs to see rather than
                      // go looking for.
                      defaultOpen={!!section.note}
                      className="border-t border-gray-200 px-4 py-6"
                    >
                      {({ open }) => (
                        <>
                          <h3 className="-mx-2 -my-3 flow-root">
                            <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                              <span className="font-medium text-gray-900">
                                {section.name}
                              </span>
                              <span className="ml-6 flex items-center">
                                {open ? (
                                  <MinusIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <PlusIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )}
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel className="pt-6">
                            {section.note && (
                              <p className="mb-3 text-xs text-gray-600">
                                {section.note}
                              </p>
                            )}
                            {section.type === "checkbox" && (
                              <SimpleCheckboxFilterContent
                                options={section.options!}
                                appliedFilters={props.appliedFilters}
                                onFilterChange={(optionValue) =>
                                  toggleSectionFilter(section.id, optionValue)
                                }
                                sectionId={section.id}
                              />
                            )}
                            {section.id === "material" && (
                              <MaterialFilterContent
                                materialSuggestions={materialSuggestions}
                                query={props.query || ""}
                                appliedFilters={props.appliedFilters.filter(
                                  (f) => f.filter === "material",
                                )}
                                onChanged={(newValues) =>
                                  updateAllSectionFilters(
                                    section.id,
                                    newValues.map((f) => f.value),
                                  )
                                }
                              />
                            )}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop filters */}
      <form className="hidden lg:block">
        {process.env.REACT_APP_SHOW_FILTERS_TITLE === "true" && (
          <h3 className="text-sm font-bold text-gray-700 pb-2 border-b-[4px]">
            <FunnelIcon className="inline-block w-4 h-4 mr-1" />
            Filters
          </h3>
        )}
        {filters.map((section) => (
          <Disclosure
            as="div"
            key={section.id}
            defaultOpen={!!section.note}
            className="border-b border-gray-200 py-6"
          >
            {({ open }) => (
              <>
                <h3 className="-my-3 flow-root">
                  <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                    <span className="font-medium text-gray-900">
                      {section.name}
                    </span>
                    <span className="ml-6 flex items-center">
                      {open ? (
                        <MinusIcon className="h-5 w-5" aria-hidden="true" />
                      ) : (
                        <PlusIcon className="h-5 w-5" aria-hidden="true" />
                      )}
                    </span>
                  </Disclosure.Button>
                </h3>
                <Disclosure.Panel className="pt-6">
                  {section.note && (
                    <p className="mb-3 text-xs text-gray-600">{section.note}</p>
                  )}
                  {section.type === "checkbox" && (
                    <SimpleCheckboxFilterContent
                      options={section.options!}
                      appliedFilters={props.appliedFilters}
                      onFilterChange={(optionValue) =>
                        toggleSectionFilter(section.id, optionValue)
                      }
                      sectionId={section.id}
                    />
                  )}
                  {section.id === "material" && (
                    <MaterialFilterContent
                      materialSuggestions={materialSuggestions}
                      query={props.query || ""}
                      appliedFilters={props.appliedFilters.filter(
                        (f) => f.filter === "material",
                      )}
                      onChanged={(newValues) =>
                        updateAllSectionFilters(
                          section.id,
                          newValues.map((f) => f.value),
                        )
                      }
                    />
                  )}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ))}
      </form>
    </div>
  );
};

type FilterPanelProps = {
  mobileFiltersOpen: boolean;
  appliedFilters: AppliedFilter[];
  onChanged: (appliedFilters: AppliedFilter[]) => void;
  setMobileFiltersOpen: (open: boolean) => void;
  structuredQuery?: any;
  query?: string;
};

export type AppliedFilter = {
  filter: string;
  value: string;
};

type SimpleCheckboxFilterContentProps = {
  options: { value: string; label: string; checked: boolean }[];
  appliedFilters: AppliedFilter[];
  onFilterChange: (optionValue: string) => void;
  sectionId: string;
};

const SimpleCheckboxFilterContent = (
  props: SimpleCheckboxFilterContentProps,
) => {
  return (
    <div className="space-y-4">
      {props.options.map((option, optionIdx) => (
        <div key={option.value} className="flex items-center">
          <input
            id={`filter-${props.sectionId}-${optionIdx}`}
            name={`${props.sectionId}[]`}
            defaultValue={option.value}
            type="checkbox"
            checked={option.checked}
            onChange={(e) => props.onFilterChange(option.value)}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label
            htmlFor={`filter-${props.sectionId}-${optionIdx}`}
            className="ml-3 text-sm text-gray-600"
          >
            {option.label}
          </label>
        </div>
      ))}
    </div>
  );
};

type MaterialFilterContentProps = {
  materialSuggestions: string[];
  query: string;
  appliedFilters: AppliedFilter[];
  onChanged: (appliedFilters: AppliedFilter[]) => void;
};

const MaterialFilterContent = (props: MaterialFilterContentProps) => {
  return (
    <div className="space-y-4">
      <MaterialMultiSelect
        onChange={(selectedMaterials) => {
          console.log("selectedMaterials", selectedMaterials);
          props.onChanged(
            selectedMaterials.map((m) => ({ filter: "material", value: m })),
          );
        }}
        appliedFilters={props.appliedFilters}
        materialSuggestions={props.materialSuggestions}
        query={props.query}
      />
      {props.materialSuggestions.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-gray-500 mb-2">
            Suggested:
          </div>
          {props.materialSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              onClick={(e) => {
                e.preventDefault();
                props.onChanged([
                  ...props.appliedFilters,
                  { filter: "material", value: suggestion },
                ]);
              }}
              className="text-xs text-gray-500 border border-gray-500 hover:bg-gray-100 inline-block px-2 py-1 rounded-full"
            >
              {suggestion} | +
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

type MaterialMultiSelectProps = {
  onChange: (selectedMaterials: string[]) => void;
  appliedFilters: AppliedFilter[];
  materialSuggestions: string[];
  query: string;
};

type MaterialSelectOption = {
  value: string;
  label: string;
};

type MaterialSelectOptionGroup = {
  label: string;
  options: MaterialSelectOption[];
};

function MaterialMultiSelect({
  onChange,
  appliedFilters,
  materialSuggestions,
  query,
}: MaterialMultiSelectProps) {
  const [selectedMaterials, setSelectedMaterials] = useState<
    MaterialSelectOption[]
  >([]);
  const [defaultOptions, setDefaultOptions] = useState<
    MaterialSelectOptionGroup[]
  >([]);
  const queryTokens = query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);
  const suggestionTokens = materialSuggestions
    .map((suggestion) => suggestion.toLowerCase().trim())
    .filter((token) => token.length > 0);
  const preferredMaterialSearchKey = Array.from(
    new Set([...suggestionTokens, ...queryTokens]),
  ).join("|");

  const addToSelection = (material: MaterialSelectOption) => {
    const newSelectedMaterials = [...selectedMaterials];
    if (!selectedMaterials.find((m) => m.value === material.value)) {
      newSelectedMaterials.push(material);
    }
    setSelectedMaterials(newSelectedMaterials);
    return newSelectedMaterials;
  };

  const removeSelection = (material: MaterialSelectOption) => {
    const newSelectedMaterials = selectedMaterials.filter(
      (m) => m.value !== material.value,
    );
    setSelectedMaterials(newSelectedMaterials);
    return newSelectedMaterials;
  };

  useEffect(() => {
    const materialFilters = appliedFilters.filter(
      (f) => f.filter === "material",
    );
    if (materialFilters.length > 0) {
      setSelectedMaterials(
        materialFilters.map((f) => ({ value: f.value, label: f.value })),
      );
    }
  }, [appliedFilters]);

  useEffect(() => {
    const loadMaterialTree = async () => {
      const preferredMaterialTokens = preferredMaterialSearchKey
        .split("|")
        .map((token) => token.trim())
        .filter((token) => token.length > 0);
      const response = await sendAuthenticatedRequest(
        `${process.env.REACT_APP_SEARCH_JS_API_BASE_URL}/materials/tree`,
        {},
      );
      const data = await response.json();
      const tree = Array.isArray(data?.tree) ? data.tree : [];
      const groupedOptions: MaterialSelectOptionGroup[] = tree
        .map((node: any) => {
          const parent =
            typeof node?.less_specific === "string" ? node.less_specific : "";
          const children = Array.isArray(node?.more_specific_list)
            ? node.more_specific_list
            : [];
          if (!parent) {
            return null;
          }
          const dedupedChildren = Array.from(new Set(children)).slice(0, 20);
          return {
            label: parent,
            options: [
              { value: parent, label: parent },
              ...dedupedChildren.map((child) => ({
                value: child,
                label: `${parent} > ${child}`,
              })),
            ],
          };
        })
        .filter((group: MaterialSelectOptionGroup | null) => !!group);
      if (preferredMaterialTokens.length === 0) {
        setDefaultOptions(groupedOptions);
        return;
      }

      const searchableText = (group: MaterialSelectOptionGroup) =>
        [group.label, ...group.options.map((option) => option.value)]
          .join(" ")
          .toLowerCase();

      const tokenMatchedGroups = groupedOptions.filter((group) =>
        preferredMaterialTokens.some((token) =>
          searchableText(group).includes(token),
        ),
      );

      // If we have direct token matches (e.g. "inconel"), include additional
      // groups that share option values with those matches (e.g. UNS aliases).
      const tokenMatchedValues = new Set(
        tokenMatchedGroups.flatMap((group) =>
          group.options.map((option) => option.value.toLowerCase()),
        ),
      );

      const relevantGroups =
        tokenMatchedGroups.length > 0
          ? groupedOptions.filter(
              (group) =>
                group.options.some((option) =>
                  tokenMatchedValues.has(option.value.toLowerCase()),
                ) ||
                tokenMatchedGroups.some((matchedGroup) =>
                  group.options.some((option) =>
                    matchedGroup.options.some(
                      (matchedOption) =>
                        matchedOption.value.toLowerCase() ===
                        option.value.toLowerCase(),
                    ),
                  ),
                ),
            )
          : groupedOptions;

      const sortedOptions = [...relevantGroups].sort((a, b) => {
        const aText = searchableText(a);
        const bText = searchableText(b);
        const aScore = preferredMaterialTokens.some((token) =>
          aText.includes(token),
        )
          ? 0
          : 1;
        const bScore = preferredMaterialTokens.some((token) =>
          bText.includes(token),
        )
          ? 0
          : 1;
        if (aScore !== bScore) {
          return aScore - bScore;
        }
        return a.label.localeCompare(b.label);
      });
      setDefaultOptions(sortedOptions);
    };
    loadMaterialTree().catch((error) => {
      console.error("Failed to preload material tree", error);
      setDefaultOptions([]);
    });
  }, [preferredMaterialSearchKey]);

  const loadMaterials = async (inputValue: string) => {
    if (!inputValue.trim()) {
      return defaultOptions;
    }
    const response = await sendAuthenticatedRequest(
      `${process.env.REACT_APP_SEARCH_JS_API_BASE_URL}/materials?q=${inputValue}`,
      {},
    );
    const data = await response.json();
    return data.map((material: string) => ({
      value: material,
      label: material,
    }));
  };

  return (
    <div>
      <AsyncSelect
        key={`${JSON.stringify(
          selectedMaterials,
        )}|${preferredMaterialSearchKey}`}
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={loadMaterials}
        // Render the dropdown in a portal so the scrollable filters sidebar
        // (overflow-y-auto) does not clip the options menu.
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        onChange={(selectedOption) => {
          if (!selectedOption || !("value" in selectedOption)) {
            return;
          }
          const selectedMaterial: MaterialSelectOption = {
            value: String(selectedOption.value),
            label: String(selectedOption.label),
          };
          console.log("selectedOption", selectedOption);
          const allMaterials = addToSelection(selectedMaterial);
          onChange(allMaterials.map((o: any) => o.value));
        }}
      />
      <div className="mt-2">
        {selectedMaterials.map((material) => (
          <button
            key={material.value}
            onClick={(e) => {
              e.preventDefault();
              const allMaterials = removeSelection(material);
              onChange(allMaterials.map((o: any) => o.value));
            }}
            className="text-xs text-gray-100 bg-gray-700 hover:bg-gray-900 inline-block px-2 py-1 rounded-full mr-1"
          >
            {material.label} | -
          </button>
        ))}
      </div>
    </div>
  );
}
